import { useMemo, useState } from "react";
import { useI18n } from "../i18n/LanguageContext";
import { LocationCard } from "./LocationCard";
import { LocationDetail } from "./LocationDetail";
import { LanguageToggle } from "./LanguageToggle";
import {
  aggregateStatus,
  locationHasAc,
  locationHasDc,
  locationMaxKw,
} from "../utils/format";
import { BRAND } from "../config/brand";
import type { LocationWithDistance } from "../App";
import type { TKey } from "../i18n/translations";

export type FilterState = {
  q: string;
  power: "all" | "ac" | "dc";
  minKw: number;
  onlyAvailable: boolean;
  /** When true → forces power=dc + minKw>=150 regardless of individual fields. */
  ultraOnly: boolean;
};

const initial: FilterState = {
  q: "",
  power: "all",
  minKw: 0,
  onlyAvailable: false,
  ultraOnly: false,
};

export function Sidebar({
  items,
  selectedId,
  onSelect,
  loading,
  error,
  onRetry,
  userActive,
  onLocateClick,
  geoBusy,
  geoError,
}: {
  items: LocationWithDistance[];
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  loading: boolean;
  error: Error | null;
  onRetry: () => void;
  userActive: boolean;
  onLocateClick: () => void;
  geoBusy: boolean;
  geoError: TKey | null;
}) {
  const { t } = useI18n();
  const [filters, setFilters] = useState<FilterState>(initial);

  const filtered = useMemo(() => {
    const q = filters.q.trim().toLowerCase();
    const minKw = filters.ultraOnly ? Math.max(filters.minKw, 150) : filters.minKw;
    const power = filters.ultraOnly ? "dc" : filters.power;
    const result = items.filter(({ location: l }) => {
      if (q) {
        const hay = `${l.name ?? ""} ${l.city} ${l.address}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (power === "ac" && !locationHasAc(l)) return false;
      if (power === "dc" && !locationHasDc(l)) return false;
      if (minKw > 0 && locationMaxKw(l) < minKw) return false;
      if (filters.onlyAvailable && aggregateStatus(l) !== "AVAILABLE") return false;
      return true;
    });
    if (userActive) {
      result.sort((a, b) => {
        const da = a.distanceKm ?? Infinity;
        const db = b.distanceKm ?? Infinity;
        return da - db;
      });
    }
    return result;
  }, [items, filters, userActive]);

  const selected = selectedId ? items.find((i) => i.location.id === selectedId) ?? null : null;

  const filtersDirty =
    filters.q ||
    filters.power !== "all" ||
    filters.minKw > 0 ||
    filters.onlyAvailable ||
    filters.ultraOnly;

  return (
    <aside className="flex h-full w-[380px] shrink-0 flex-col border-r border-ink-100 bg-white">
      <header className="border-b border-ink-100 px-4 pt-4 pb-3">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <Logo />
            <div className="leading-tight">
              <div className="text-[14px] font-semibold text-ink-900">{t("appName")}</div>
              <div className="text-[11px] text-ink-500">{t("appSubtitle")}</div>
            </div>
          </div>
          <LanguageToggle />
        </div>
      </header>

      {selected ? (
        <LocationDetail
          location={selected.location}
          distanceKm={selected.distanceKm}
          onBack={() => onSelect(null)}
        />
      ) : (
        <>
          <div className="border-b border-ink-100 px-4 py-3 space-y-2.5">
            <SearchInput
              value={filters.q}
              onChange={(q) => setFilters((f) => ({ ...f, q }))}
              placeholder={t("search")}
            />
            <div className="flex items-center gap-1.5">
              <SegBtn
                active={filters.power === "all" && !filters.ultraOnly}
                onClick={() => setFilters((f) => ({ ...f, power: "all", ultraOnly: false }))}
              >
                {t("all")}
              </SegBtn>
              <SegBtn
                active={filters.power === "ac" && !filters.ultraOnly}
                onClick={() => setFilters((f) => ({ ...f, power: "ac", ultraOnly: false }))}
              >
                {t("ac")}
              </SegBtn>
              <SegBtn
                active={filters.power === "dc" && !filters.ultraOnly}
                onClick={() => setFilters((f) => ({ ...f, power: "dc", ultraOnly: false }))}
              >
                {t("dc")}
              </SegBtn>

              <button
                onClick={() => setFilters((f) => ({ ...f, ultraOnly: !f.ultraOnly }))}
                aria-pressed={filters.ultraOnly}
                className={
                  "ml-1 inline-flex items-center gap-1 rounded-md px-2 py-1 text-[11px] font-semibold transition " +
                  (filters.ultraOnly
                    ? "bg-[#6E3FB8] text-white shadow-soft"
                    : "bg-[#F1ECF8] text-[#5C2A9F] hover:bg-[#E6DBF3]")
                }
              >
                <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                  <path d="M13 2L4 14h7l-1 8 9-12h-7l1-8z" />
                </svg>
                {t("dcUltraPreset")}
              </button>

              <label className="ml-auto flex items-center gap-1.5 text-[11.5px] text-ink-600">
                <input
                  type="checkbox"
                  checked={filters.onlyAvailable}
                  onChange={(e) =>
                    setFilters((f) => ({ ...f, onlyAvailable: e.target.checked }))
                  }
                  className="h-3.5 w-3.5 accent-brand-500"
                />
                {t("statusAVAILABLE")}
              </label>
            </div>
            <div>
              <div className="mb-1 flex items-center justify-between text-[11px] text-ink-500">
                <span>{t("minPower")}</span>
                <span className="font-medium text-ink-700 tabular-nums">
                  {filters.ultraOnly
                    ? "≥ 150 kW"
                    : filters.minKw > 0
                      ? `${filters.minKw} kW`
                      : t("all")}
                </span>
              </div>
              <input
                type="range"
                min={0}
                max={360}
                step={10}
                value={filters.ultraOnly ? Math.max(filters.minKw, 150) : filters.minKw}
                disabled={filters.ultraOnly}
                onChange={(e) =>
                  setFilters((f) => ({ ...f, minKw: Number(e.target.value) }))
                }
                className="w-full accent-brand-500 disabled:opacity-50"
              />
            </div>

            {/* Near-me + sort row */}
            <div className="flex items-center justify-between gap-2 pt-0.5">
              <button
                onClick={onLocateClick}
                className={
                  "inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-[11.5px] font-medium transition " +
                  (userActive
                    ? "bg-brand-500 text-white shadow-soft"
                    : "bg-ink-50 text-ink-700 hover:bg-ink-100")
                }
              >
                {geoBusy ? (
                  <Spinner />
                ) : (
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden>
                    <circle cx="12" cy="12" r="3.4" fill="currentColor" />
                    <circle cx="12" cy="12" r="7.2" stroke="currentColor" strokeWidth="1.6" />
                    <path d="M12 2v3M12 19v3M2 12h3M19 12h3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                  </svg>
                )}
                {userActive ? t("sortNearest") : t("nearMe")}
              </button>
              {geoError && <span className="text-[11px] text-red-600">{t(geoError)}</span>}
            </div>
          </div>

          <div className="flex items-center justify-between px-4 py-2 text-[11.5px] text-ink-500 border-b border-ink-100">
            <span>
              <span className="font-semibold text-ink-900 tabular-nums">{filtered.length}</span>{" "}
              {t("locations")}
            </span>
            {filtersDirty && (
              <button
                onClick={() => setFilters(initial)}
                className="text-brand-600 hover:text-brand-700 font-medium"
              >
                {t("reset")}
              </button>
            )}
          </div>

          <div className="flex-1 overflow-y-auto px-3 py-3 space-y-2">
            {loading && <Skeletons />}
            {error && !loading && (
              <div className="rounded-xl border border-red-100 bg-red-50 p-4 text-center">
                <p className="text-[13px] font-medium text-red-700">{t("errorTitle")}</p>
                <p className="mt-0.5 text-[11px] text-red-600">{error.message}</p>
                <button onClick={onRetry} className="btn-primary mt-3">
                  {t("errorRetry")}
                </button>
              </div>
            )}
            {!loading && !error && filtered.length === 0 && (
              <div className="py-12 text-center text-[13px] text-ink-400">
                {t("noResults")}
              </div>
            )}
            {!loading &&
              filtered.map(({ location, distanceKm }) => (
                <LocationCard
                  key={location.id}
                  location={location}
                  distanceKm={distanceKm}
                  active={selectedId === location.id}
                  onClick={() => onSelect(location.id)}
                />
              ))}
          </div>
        </>
      )}

      <footer className="border-t border-ink-100 px-4 py-2 text-[10.5px] text-ink-400">
        {t("poweredBy")}
      </footer>
    </aside>
  );
}

function SearchInput({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
}) {
  return (
    <div className="relative">
      <svg
        className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-ink-400"
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        aria-hidden
      >
        <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
        <path d="M20 20l-3.5-3.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      </svg>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full rounded-lg border border-ink-100 bg-ink-50 py-2 pl-9 pr-3 text-[13px] text-ink-900 placeholder:text-ink-400 outline-none focus:border-brand-300 focus:bg-white focus:ring-4 focus:ring-brand-100"
      />
    </div>
  );
}

function SegBtn({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={
        "rounded-md px-2.5 py-1 text-[11.5px] font-medium transition " +
        (active
          ? "bg-ink-900 text-white"
          : "bg-ink-50 text-ink-600 hover:bg-ink-100")
      }
    >
      {children}
    </button>
  );
}

function Spinner() {
  return (
    <svg className="animate-spin" width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden>
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" opacity="0.25" />
      <path d="M21 12a9 9 0 0 0-9-9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function Skeletons() {
  return (
    <div className="space-y-2">
      {[0, 1, 2, 3, 4].map((i) => (
        <div
          key={i}
          className="h-[78px] rounded-xl bg-gradient-to-r from-ink-50 via-ink-100 to-ink-50 animate-pulse"
        />
      ))}
    </div>
  );
}

function Logo() {
  return (
    <div
      className="grid h-9 w-9 place-items-center rounded-xl text-white shadow-soft"
      style={{ background: `linear-gradient(135deg, ${BRAND.primary} 0%, ${BRAND.primaryDark} 100%)` }}
      aria-hidden
    >
      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
        <path d="M13 2L4 14h7l-1 8 9-12h-7l1-8z" />
      </svg>
    </div>
  );
}
